import React, { useContext } from 'react';
import Loadable from 'react-loadable';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Spinner from '../../../components/GifSpinner';
import PropertyNotSelectedPage from '../../../components/PropertyNotSelectedPage';
import ForbiddenPage from '../../../components/ForbiddenPage';
import NotFound from '../../../components/NotFound';
import ForgotPassword from '../../ForgotPassword';
import LoginPage from '../../Login';
import SecondaryLeaseApplicationPage from '../../NonPrimaryLeaseApplication';
import PrivacyPolicyPage from '../../PrivacyPolicy';
import RecertificationApplication from '../../RecertificationApplication';
import ResetPasswordPage from '../../ResetPassword';
import ShortLeaseApplicationPage from '../../ShortLeaseApplication';
import TermsOfUsePage from '../../TermsOfUse';
import MainLayoutRoutes from './MainLayoutRoutes';
import LeaseApplicationForm from '../../LeaseApplicationForm';
import RecertApplicationForm from '../../RecertApplicationForm';
import { AppContext } from '../context';

const LoadablePrimaryLeaseApplication = Loadable({
  loader: () => import('../../PrimaryLeaseApplication'),
  loading: Spinner,
});

export function withApplicationFormRedirects(Component, isRevampForm = false) {
  return (props) => {
    const { appFormRevamp } = useFlags();
    const { isLdUserContextReady, selectedProperty } = useContext(AppContext);

    // Wait for LaunchDarkly to be ready before making redirect decisions
    if (!isLdUserContextReady) {
      return null;
    }

    // If feature flag is ON and this is an old form route, redirect to new application form
    if (appFormRevamp && !isRevampForm) {
      const { match } = props;
      const { propertyId, applicationId, applicantId } = match.params;
      return (
        <Redirect
          to={`/property/${propertyId}/application-form/${applicationId}/${applicantId}`}
        />
      );
    }

    // If feature flag is OFF and this is the new form route, redirect to 404
    if (!appFormRevamp && isRevampForm) {
      return <Redirect to="/404" />;
    }

    // App selects the property from the URL only when it is in the user's
    // property list, so a link to a property this user cannot access leaves
    // nothing selected. The new form reads the property straight from context
    // and would crash on it, so send the user to the property picker instead,
    // as the profile pages do. Decided after the flag checks so a flag-off
    // request keeps landing on /404.
    if (isRevampForm && !selectedProperty) {
      return <Redirect to="/select-property" />;
    }

    // Otherwise, render the component normally
    return <Component {...props} />;
  };
}

// The Recert Application form only exists behind the recert-application-revamp flag.
export function withRecertApplicationRevampGate(Component) {
  return (props) => {
    const { recertApplicationRevamp } = useFlags();
    const { isLdUserContextReady, selectedProperty } = useContext(AppContext);

    if (!isLdUserContextReady) {
      return null;
    }
    if (!recertApplicationRevamp) {
      return <Redirect to="/404" />;
    }
    // The form reads the property from context; a property this user cannot access leaves
    // nothing selected.
    if (!selectedProperty) {
      return <Redirect to="/select-property" />;
    }
    return <Component {...props} />;
  };
}

const GatedRecertApplicationForm = withRecertApplicationRevampGate(
  RecertApplicationForm,
);

function Routes() {
  return (
    <Switch>
      <Route exact path="/reset-password" component={ResetPasswordPage} />
      <Route exact path="/login" component={LoginPage} />
      <Route exact path="/termsofuse" component={TermsOfUsePage} />
      <Route exact path="/privacypolicy" component={PrivacyPolicyPage} />
      <Route exact path="/forbidden" component={ForbiddenPage} />
      <Route exact path="/404" component={NotFound} />
      <Route
        exact
        path="/select-property"
        component={PropertyNotSelectedPage}
      />
      <Route exact path="/forgot-password" component={ForgotPassword} />
      <Route
        exact
        path="/property/:propertyId/shortapplication/:applicationId/:applicantId"
        component={withApplicationFormRedirects(ShortLeaseApplicationPage)}
      />
      <Route
        exact
        // eslint-disable-next-line
        path="/property/:propertyId/affordable-qualifications/:affordableQualificationId/affordable-programs/:programName/households/:householdId/applications/:applicationId/applicants/:applicantId/residents/:residentId/recertification-application"
        component={RecertificationApplication}
      />
      <Route
        exact
        // eslint-disable-next-line
        path="/property/:propertyId/affordable-qualifications/:affordableQualificationId/affordable-programs/:programName/households/:householdId/applicants/:applicantId/residents/:residentId/recert-application"
        component={GatedRecertApplicationForm}
      />
      <Route
        exact
        path="/property/:propertyId/primary-form/:applicationId/:applicantId"
        component={withApplicationFormRedirects(
          LoadablePrimaryLeaseApplication,
        )}
      />
      <Route
        exact
        path="/property/:propertyId/non-primary-form/:applicationId/:applicantId"
        component={withApplicationFormRedirects(SecondaryLeaseApplicationPage)}
      />
      <Route
        exact
        path="/property/:propertyId/application-form/:applicationId/:applicantId"
        component={withApplicationFormRedirects(LeaseApplicationForm, true)}
      />
      <MainLayoutRoutes />
    </Switch>
  );
}

export default Routes;
